import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Download, FileText, TrendingUp, Users, Plane, Calendar,
  DollarSign, Percent, Activity,
} from 'lucide-react';
import { format } from 'date-fns';
import StatCard from '../components/StatCard';
import { getReportingData, PROFIT_RATE } from '../firebase/firestore';

const COLORS = ['#2D5BE3', '#9B59B6', '#00C853', '#D4AF37', '#FF1744'];

function exportCsv(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map(r => headers.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

export default function Reporting() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('6m');

  useEffect(() => {
    setLoading(true);
    getReportingData(range)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [range]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#2D5BE3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-[#546E7A]">Failed to load reporting data. Check Firebase connection.</p>;
  }

  const handleExportBookings = () => {
    exportCsv(
      data.recentBookings.map(b => ({
        ref: b.bookingRef || b.id,
        type: b.type,
        status: b.status,
        total: b.totalPrice,
        user: b.userId,
        date: b.bookedAtLabel,
      })),
      `metamax-bookings-${format(new Date(), 'yyyy-MM-dd')}.csv`,
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#1A3C8F]/40 to-[#0D1F4F]/60 border border-[#2D5BE3]/30 rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/metamax-logo.png" alt="MetaMax" className="w-14 h-14 object-contain rounded-xl" />
            <div>
              <h2 className="text-white font-bold text-xl tracking-widest">META MAX</h2>
              <p className="text-[#D4AF37] text-sm font-medium">TRAVEL BEYOND LIMITS</p>
              <p className="text-[#546E7A] text-sm">Revenue, bookings, and user metrics from Firestore</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[#546E7A] text-xs">Report generated</div>
            <div className="text-white text-sm font-semibold">{new Date().toLocaleDateString('en-US', { dateStyle: 'long' })}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {[
            { id: '3m', label: '3 months' },
            { id: '6m', label: '6 months' },
            { id: '12m', label: '12 months' },
          ].map(r => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors
                ${range === r.id ? 'bg-[#2D5BE3] text-white' : 'bg-[#1A2235] border border-[#1E2D45] text-[#546E7A] hover:text-white'}`}
            >
              {r.label}
            </button>
          ))}
          <button
            onClick={handleExportBookings}
            className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-[#1A2235] border border-[#1E2D45]
              text-[#B0BEC5] hover:text-white text-xs font-semibold transition-colors"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Gross Revenue" value={`$${data.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          icon={DollarSign} color="#D4AF37" />
        <StatCard title="Platform Profit" value={`$${data.platformProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          sub={`${PROFIT_RATE * 100}% commission`} icon={TrendingUp} color="#00C853" />
        <StatCard title="Avg. Booking" value={`$${data.avgBookingValue.toFixed(0)}`}
          icon={Percent} color="#2D5BE3" />
        <StatCard title="New Users (period)" value={data.newUsersInRange}
          icon={Users} color="#9B59B6" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-1">Bookings by Month</h3>
          <p className="text-[#546E7A] text-xs mb-4">Flight vs event volume</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.bookingsByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D45" />
              <XAxis dataKey="month" stroke="#546E7A" tick={{ fontSize: 11 }} />
              <YAxis stroke="#546E7A" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 12 }} />
              <Legend />
              <Bar dataKey="flights" name="Flights" fill="#2D5BE3" radius={[4, 4, 0, 0]} />
              <Bar dataKey="events" name="Events" fill="#9B59B6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-1">Revenue by Type</h3>
          <p className="text-[#546E7A] text-xs mb-4">Flight vs event revenue split</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={data.revenueByType}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {data.revenueByType.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={v => [`$${Number(v).toLocaleString()}`, 'Revenue']}
                contentStyle={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-1">User Signups</h3>
          <p className="text-[#546E7A] text-xs mb-4">New registrations over time</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.userSignupsByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D45" />
              <XAxis dataKey="month" stroke="#546E7A" tick={{ fontSize: 11 }} />
              <YAxis stroke="#546E7A" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 12 }} />
              <Line type="monotone" dataKey="users" stroke="#00C853" strokeWidth={2} dot={{ fill: '#00C853' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-4">Booking Status Summary</h3>
          <div className="space-y-4">
            {[
              { label: 'Confirmed', value: data.confirmed, total: data.totalBookings, color: '#00C853', icon: Plane },
              { label: 'Cancelled', value: data.cancelled, total: data.totalBookings, color: '#FF1744', icon: Calendar },
              { label: 'Pending / Other', value: data.pending, total: data.totalBookings, color: '#D4AF37', icon: Activity },
            ].map(({ label, value, total, color }) => (
              <div key={label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#B0BEC5]">{label}</span>
                  <span className="text-white font-semibold">
                    {value} ({total ? Math.round((value / total) * 100) : 0}%)
                  </span>
                </div>
                <div className="h-2 bg-[#1E2D45] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${total ? (value / total) * 100 : 0}%`, background: color }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-[#1E2D45] grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold text-[#2D5BE3]">{data.catalogFlights}</div>
              <div className="text-[#546E7A] text-xs">Flights in catalog</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#9B59B6]">{data.catalogEvents}</div>
              <div className="text-[#546E7A] text-xs">Events in catalog</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-[#2D5BE3]" />
            <h3 className="text-white font-semibold">Recent Transactions</h3>
          </div>
          <span className="text-[#546E7A] text-xs">Last {data.recentBookings.length} bookings</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#546E7A] text-left border-b border-[#1E2D45]">
                <th className="pb-3 pr-4">Reference</th>
                <th className="pb-3 pr-4">Type</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 pr-4">Amount</th>
                <th className="pb-3 pr-4">Profit</th>
                <th className="pb-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.recentBookings.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[#546E7A]">
                    No bookings yet. Bookings from the Flutter app appear here.
                  </td>
                </tr>
              ) : (
                data.recentBookings.map(b => (
                  <tr key={b.id} className="border-b border-[#1E2D45]/50 text-[#B0BEC5]">
                    <td className="py-3 pr-4 text-white font-mono text-xs">{b.bookingRef || b.id?.slice(0, 8)}</td>
                    <td className="py-3 pr-4 capitalize">{b.type || '—'}</td>
                    <td className="py-3 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs capitalize
                        ${b.status === 'confirmed' ? 'bg-green-500/10 text-green-400' :
                          b.status === 'cancelled' ? 'bg-red-500/10 text-red-400' :
                          'bg-yellow-500/10 text-yellow-400'}`}>
                        {b.status || 'pending'}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-[#D4AF37]">${(b.totalPrice || 0).toLocaleString()}</td>
                    <td className="py-3 pr-4 text-green-400">
                      ${((b.totalPrice || 0) * PROFIT_RATE).toFixed(0)}
                    </td>
                    <td className="py-3">{b.bookedAtLabel}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
